import {
  faArrowRight,
  faArrowUpRight,
  faRectangleList,
} from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";

import { DateTimePickerDropdown } from "src/base-components/DateTimePickerDropdown";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Input } from "src/base-components/Input";
import { Select } from "src/base-components/Select";
import { useGetCasePropertyIcon } from "src/cases/common/useGetCasePropertyIcon";
import {
  CreateCaseFormValues,
  getCreateCaseStepById,
} from "src/cases/createCase/createCaseSteps";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { CasePropertiesValue } from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { findSchema } from "src/entities/entityView/utils";
import { useEntitySchemas } from "src/entities/queries";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { findEventSchema } from "src/eventsCatalogue/utils";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import {
  getUrlToEventsPage,
  getUrlToSchemaEntitiesPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type CreateCasePropertiesStepProps = {
  caseProperties: Record<string, CasePropertiesValue> | undefined;
};

export const CreateCasePropertiesStep: React.FC<
  CreateCasePropertiesStepProps
> = ({ caseProperties }) => {
  const step = getCreateCaseStepById("properties");
  const getPropertyIcon = useGetCasePropertyIcon();
  const form = useFormContext<CreateCaseFormValues>();
  const { orgId, wsId } = useParamsDecode<{ orgId: string; wsId: string }>();
  const featureGates = useWorkspaceFeatureGates();
  const { workspace } = useWorkspaceContext();
  const { data: entitySchemas } = useEntitySchemas({
    baseUrl: workspace.base_url!,
    options: { enabled: featureGates.entitiesEnabled },
  });
  const { data: eventSchemas } = useEventSchemas();

  const entries = caseProperties ? Object.entries(caseProperties) : [];

  return (
    <FormStepWrapper description={step.description} title={step.formTitle}>
      {entries.length === 0 ? (
        <EmptyState
          description="No properties have been configured for this case template"
          headline="No properties"
          icon={faRectangleList}
          variant="default"
        />
      ) : (
        <div className="flex flex-col gap-3">
          {entries.map(([key, property]) => (
            <PropertyRow
              key={key}
              entitySchemas={entitySchemas}
              eventSchemas={eventSchemas}
              form={form}
              getPropertyIcon={getPropertyIcon}
              orgId={orgId}
              property={property}
              propertyKey={key}
              wsId={wsId}
            />
          ))}
        </div>
      )}
    </FormStepWrapper>
  );
};

const PropertyRow: React.FC<{
  propertyKey: string;
  property: CasePropertiesValue;
  form: ReturnType<typeof useFormContext<CreateCaseFormValues>>;
  getPropertyIcon: ReturnType<typeof useGetCasePropertyIcon>;
  entitySchemas: ReturnType<typeof useEntitySchemas>["data"];
  eventSchemas: ReturnType<typeof useEventSchemas>["data"];
  orgId: string;
  wsId: string;
}> = ({
  propertyKey,
  property,
  form,
  getPropertyIcon,
  entitySchemas,
  eventSchemas,
  orgId,
  wsId,
}) => {
  const icon = getPropertyIcon(property);

  const linkTooltip = (() => {
    if (property.type === "entity_ref") {
      const schema = findSchema(property.entity_type, entitySchemas);
      const name = schema?._display_name_plural ?? property.entity_type;
      return `Open ${name} in new tab`;
    }
    if (property.type === "event_ref") {
      const schema = findEventSchema(property.event_type, eventSchemas);
      const name = schema?.display_name_plural ?? property.event_type;
      return `Open ${name} in new tab`;
    }
    return undefined;
  })();

  return (
    <div className="flex w-full items-start">
      <div className="w-1/2">
        <Input
          disabled
          fullWidth
          placeholder={property.display_name}
          prefixIcon={{ icon }}
        />
      </div>
      <div className="mx-3 mt-1.5 flex-shrink-0">
        <Icon icon={faArrowRight} size="xs" />
      </div>
      <div className="flex w-1/2 items-center gap-3">
        <div className="min-w-0 flex-1">
          <PropertyInput
            form={form}
            property={property}
            propertyKey={propertyKey}
          />
        </div>
        {property.type === "entity_ref" && (
          <Tooltip title={linkTooltip} asChild>
            <Link
              className="flex-shrink-0 text-gray-500 hover:text-indigo-600"
              rel="noreferrer"
              target="_blank"
              to={getUrlToSchemaEntitiesPage(orgId, wsId, property.entity_type)}
            >
              <Icon icon={faArrowUpRight} size="xs" />
            </Link>
          </Tooltip>
        )}
        {property.type === "event_ref" && (
          <Tooltip title={linkTooltip} asChild>
            <Link
              className="flex-shrink-0 text-gray-500 hover:text-indigo-600"
              rel="noreferrer"
              target="_blank"
              to={getUrlToEventsPage({
                orgId,
                wsId,
                eventType: property.event_type,
              })}
            >
              <Icon icon={faArrowUpRight} size="xs" />
            </Link>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

const PropertyInput: React.FC<{
  propertyKey: string;
  property: CasePropertiesValue;
  form: ReturnType<typeof useFormContext<CreateCaseFormValues>>;
}> = ({ propertyKey, property, form }) => {
  const fieldName = `case_properties.${propertyKey}` as const;
  const fieldError = form.formState.errors.case_properties?.[propertyKey];
  const errored = !!fieldError;
  const errorMessage =
    fieldError?.message && typeof fieldError.message === "string"
      ? fieldError.message
      : undefined;

  const renderInput = () => {
    switch (property.type) {
      case "string":
      case "integer":
      case "number":
        return (
          <Input
            errored={errored}
            fullWidth
            placeholder="Enter value"
            {...form.register(fieldName)}
          />
        );

      case "datetime":
        return (
          <Controller
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <DateTimePickerDropdown
                errored={errored}
                placeholder="Select a date and time"
                fullWidth
                value={
                  typeof field.value === "string"
                    ? new Date(field.value)
                    : (field.value as Date | undefined)
                }
                allowFutureDates
                onChange={(value) => field.onChange(value)}
                onReset={() => field.onChange(null)}
              />
            )}
          />
        );

      case "enum":
        return (
          <Controller
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <Select
                dataLoc={`create-case-property-${propertyKey}`}
                errored={errored}
                options={property.values.map((v) => ({
                  key: v.value,
                  value: v.display_name || v.value,
                }))}
                placeholder="Select value"
                value={(field.value as string | undefined) ?? ""}
                onChange={(value) => {
                  field.onChange(field.value === value ? null : value);
                }}
              />
            )}
          />
        );

      case "tags":
        return (
          <Controller
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <Select
                dataLoc={`create-case-property-${propertyKey}`}
                errored={errored}
                options={property.values.map((v) => ({
                  key: v.value,
                  value: v.display_name || v.value,
                }))}
                placeholder="Select value(s)"
                value={(field.value as string[]) ?? []}
                multiple
                onChange={field.onChange}
              />
            )}
          />
        );

      case "entity_ref":
        return (
          <Input
            errored={errored}
            fullWidth
            placeholder="Enter Entity ID"
            {...form.register(fieldName)}
          />
        );

      case "event_ref":
        return (
          <Input
            errored={errored}
            fullWidth
            placeholder="Enter Event ID"
            {...form.register(fieldName)}
          />
        );
    }
  };

  return (
    <>
      {renderInput()}
      {errorMessage && <ErrorHint margin="none">{errorMessage}</ErrorHint>}
    </>
  );
};
